// ตารางสอน วัน × คาบ — ใช้ร่วมกันระหว่าง "ตารางสอนของฉัน" (ครูดูของตัวเอง) กับ
// "ตารางสอนครู" (ฝ่ายการเรียนการสอนดูของครูทุกคน)
//
// สองหน้านี้ต่างกันแค่ว่า t_id มาจากไหน — ของฉันมาจาก JWT ส่วนอีกหน้ามาจาก dropdown
// ตัวตารางกับไฟล์ PDF ต้องออกมาหน้าตาเดียวกัน ถ้าแยกกันเขียนจะเพี้ยนจากกันเมื่อแก้ทีหลัง
import React from "react";
import jsPDF from "jspdf";

export const DAYS = [0, 1, 2, 3, 4, 5, 6];
export const DAY_NAMES = {
  0: "อาทิตย์", 1: "จันทร์", 2: "อังคาร", 3: "พุธ", 4: "พฤหัส", 5: "ศุกร์", 6: "เสาร์",
};

const SEMESTER_COLORS = {
  "1": { bg: "#e3f2fd", border: "#1976d2", text: "#0d47a1" },
  "2": { bg: "#fce4ec", border: "#c62828", text: "#b71c1c" },
};

// รวมคาบสอนกับคาบแนะแนวเป็นผัง [วัน][period_id]
export const buildTimetableGrid = (teachingData, guidanceData, semester) => {
  const g = {};
  (teachingData || []).forEach((row) => {
    if (row.teachtime_id === null || row.teach_day === null || !row.period_id) return;
    if (String(row.sub_semester) !== String(semester)) return;
    if (!g[row.teach_day]) g[row.teach_day] = {};
    // A teacher may teach multiple subjects in same slot (edge case): keep first
    if (!g[row.teach_day][row.period_id]) {
      g[row.teach_day][row.period_id] = {
        sub_subid: row.sub_subid,
        sub_subname: row.sub_subname,
        sub_type: row.sub_type,
        c_id: row.tb_class_c_id,
        r_no: row.r_no,
      };
    }
  });
  // overlay คาบแนะแนวของเทอมที่เลือก
  (guidanceData || []).forEach((row) => {
    if (String(row.semester) !== String(semester)) return;
    if (!g[row.teach_day]) g[row.teach_day] = {};
    g[row.teach_day][row.period_id] = {
      sub_subid: "แนะแนว",
      sub_subname: "กิจกรรมแนะแนว",
      c_id: row.tb_class_c_id,
      r_no: row.r_no,
      is_guidance: true,
    };
  });
  return g;
};

// มีอะไรให้แสดงในเทอมนี้ไหม — ใช้ตัดสินทั้งข้อความ "ไม่พบตารางสอน" และปุ่ม Export
export const hasTimetableData = (teachingData, guidanceData, semester) =>
  (teachingData || []).some(
    (r) => r.teachtime_id !== null && String(r.sub_semester) === String(semester)
  ) || (guidanceData || []).some((g) => String(g.semester) === String(semester));

const SlotCell = ({ slot, semester }) => {
  if (!slot) return (
    <div style={{ minHeight: 58, color: "#ccc", fontSize: "0.7rem", textAlign: "center", paddingTop: 18 }}>—</div>
  );
  const colors = slot.is_guidance
    ? { bg: "#fff3cd", border: "#f0ad4e", text: "#8a6d3b" } // คาบแนะแนว — โทน warning
    : (SEMESTER_COLORS[semester] || SEMESTER_COLORS["1"]);
  return (
    <div style={{
      minHeight: 58, background: colors.bg, border: `1px solid ${colors.border}`,
      borderRadius: 4, padding: "4px 6px", fontSize: "0.72rem",
    }}>
      <div className="fw-semibold" style={{ color: colors.text, lineHeight: 1.2 }}>{slot.sub_subid}</div>
      <div style={{ lineHeight: 1.2, color: "#333" }}>{slot.sub_subname}</div>
      <div style={{ lineHeight: 1.2, color: "#000000", fontSize: "0.8rem" }}>ม.{slot.c_id}/{slot.r_no}</div>
    </div>
  );
};

export const TimetableGrid = ({ periods, grid, semester }) => (
  <div style={{ overflowX: "auto" }}>
    <table
      className="table table-bordered mb-0"
      style={{ tableLayout: "fixed", minWidth: periods.length * 100 + 80 }}
    >
      <thead className="table-secondary">
        <tr>
          <th style={{ width: 80, fontSize: "0.78rem" }}>วัน</th>
          {periods.map((p) => (
            <th key={p.period_id} className="text-center" style={{ fontSize: "0.73rem" }}>
              <div className="fw-semibold">คาบ {p.period_order}</div>
              <div className="text-muted fw-normal">{p.period_begin}–{p.period_end}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {DAYS.map((d) => (
          <tr key={d}>
            <td className="text-center align-middle fw-semibold" style={{ fontSize: "0.78rem" }}>
              {DAY_NAMES[d]}
            </td>
            {periods.map((p) => (
              <td key={p.period_id} style={{ padding: 3 }}>
                <SlotCell slot={grid[d]?.[p.period_id]} semester={semester} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// HTML ของตารางสำหรับพิมพ์/แปลงเป็น PDF — ใช้ร่วมกันทั้งสองทาง เพื่อให้สิ่งที่ออกจาก
// เครื่องพิมพ์กับสิ่งที่อยู่ในไฟล์ PDF เป็นหน้าเดียวกันเสมอ
const buildTimetableHTML = ({ periods, grid, teacherName, yearLabel, semester }) => {
  const periodHeaders = periods.map(
    (p) => `<th style="background:#4472c4;color:#fff;padding:6px 4px;font-size:12px;text-align:center;border:1px solid #ccc;">
              คาบ ${p.period_order}<br/><span style="font-size:10px;font-weight:normal;">${p.period_begin}–${p.period_end}</span>
             </th>`
  ).join("");

  const bodyRows = DAYS.map((d) => {
    const cells = periods.map((p) => {
      const slot = grid[d]?.[p.period_id];
      const content = slot
        ? `<div style="font-weight:600;font-size:11px;">${slot.sub_subid}</div>
           <div style="font-size:10px;color:#333;">${slot.sub_subname}</div>
           <div style="font-size:10px;color:#000;">ม.${slot.c_id}/${slot.r_no}</div>`
        : `<div style="color:#bbb;font-size:10px;text-align:center;">—</div>`;
      const cellBg = slot?.is_guidance ? "#fff3cd" : "#fff";
      return `<td style="padding:4px;border:1px solid #ccc;vertical-align:top;background:${cellBg};">${content}</td>`;
    }).join("");
    return `<tr>
      <td style="padding:6px 8px;font-weight:700;font-size:12px;background:#f2f2f2;border:1px solid #ccc;white-space:nowrap;">${DAY_NAMES[d]}</td>
      ${cells}
    </tr>`;
  }).join("");

  return `
    <div style="font-family:Tahoma,Arial,sans-serif;padding:20px;background:#fff;color:#111;">
      <h3 style="margin:0 0 4px;font-size:15px;">ตารางสอน — ${teacherName}</h3>
      <div style="margin-bottom:10px;font-size:12px;color:#555;">${yearLabel} เทอม ${semester}</div>
      <table style="border-collapse:collapse;width:100%;">
        <thead>
          <tr>
            <th style="background:#4472c4;color:#fff;padding:6px 8px;font-size:12px;border:1px solid #ccc;white-space:nowrap;">วัน</th>
            ${periodHeaders}
          </tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
    </div>`;
};

// วาดตารางเป็น HTML นอกจอแล้วแปลงเป็นภาพลง PDF — jsPDF เขียนภาษาไทยตรงๆ ไม่ได้
// ถ้าไม่ฝังฟอนต์ จึงเลี่ยงไปทาง html2canvas ซึ่งได้สระ/วรรณยุกต์ถูกตำแหน่งแน่นอน
export const exportTimetablePDF = async ({ periods, grid, teacherName, yearLabel, semester }) => {
  if (!periods.length) return;

  const html = buildTimetableHTML({ periods, grid, teacherName, yearLabel, semester });

  const el = document.createElement("div");
  el.style.cssText = "position:fixed;left:-99999px;top:0;background:#fff;";
  el.innerHTML = html;
  document.body.appendChild(el);
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(el, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pw = 841.89; const ph = 595.28;
    const imgW = pw;
    const imgH = (canvas.height * pw) / canvas.width;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    if (imgH <= ph) {
      doc.addImage(imgData, "PNG", 0, 0, imgW, imgH);
    } else {
      const scale = ph / imgH;
      doc.addImage(imgData, "PNG", 0, 0, imgW * scale, ph);
    }
    doc.save(`ตารางสอน_${teacherName}_เทอม${semester}.pdf`);
  } finally {
    document.body.removeChild(el);
  }
};

// สั่งพิมพ์ตารางโดยตรง — ส่ง HTML ตัวเดียวกับที่ใช้ทำ PDF เข้า iframe ซ่อนแล้วสั่งพิมพ์
//
// ใช้ iframe ไม่ใช่ window.open เพราะตัวบล็อกป๊อปอัพกินหน้าต่างที่เปิดใหม่บ่อย และ
// ถึงเปิดได้ก็เหลือแท็บเปล่าค้างไว้ให้ปิดเอง ส่วนการพิมพ์ทั้งหน้าเว็บด้วย window.print()
// ตรงๆ จะติดเมนู/ปุ่มของหน้าจอไปด้วย ต้องไปเขียน print stylesheet ซ่อนทีละส่วน
//
// ตั้ง onload ก่อนใส่ srcdoc เสมอ — ถ้าสั่งพิมพ์ก่อน iframe เรนเดอร์เสร็จ บางเบราว์เซอร์
// จะพ่นกระดาษเปล่าออกมา
export const printTimetable = ({ periods, grid, teacherName, yearLabel, semester }) => {
  if (!periods.length) return;

  const body = buildTimetableHTML({ periods, grid, teacherName, yearLabel, semester });
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");

  // ต้องมีขนาดจริง ห้าม 0×0 หรือ display:none — เนื้อหาที่ไม่ถูกจัดหน้าจะพิมพ์ออกมาเป็น
  // กระดาษเปล่า จึงดันออกไปนอกจอด้วย left แทนการย่อให้เหลือศูนย์
  iframe.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1024px;height:800px;border:0;visibility:hidden;";

  document.body.appendChild(iframe);

  // เขียนเนื้อหาด้วย document.write ไม่ใช่ srcdoc — write เป็น synchronous เอกสารพร้อม
  // ทันทีที่ close() คืนค่า จึงไม่ต้องพึ่ง event load ซึ่งเป็นต้นเหตุที่พิมพ์ออกมาเป็น
  // หน้าเปล่า (แทรก iframe เปล่าลง DOM แล้วเบราว์เซอร์ยิง load ของ about:blank ทันที
  // คำสั่งพิมพ์จึงไปตกใส่เอกสารว่าง) ในนี้ไม่มีรูปหรือฟอนต์ภายนอกให้ต้องรอโหลด
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html lang="th"><head><meta charset="utf-8">
    <title>ตารางสอน ${teacherName}</title>
    <style>@page { size: A4 landscape; margin: 10mm; } body { margin: 0; }</style>
    </head><body>${body}</body></html>`);
  doc.close();

  iframe.contentWindow.focus();
  iframe.contentWindow.print();

  // รอให้กล่องพิมพ์ทำงานเสร็จก่อนเก็บ iframe ทิ้ง — ลบทันทีจะทำให้บางเบราว์เซอร์
  // ยกเลิกงานพิมพ์กลางคัน
  setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 1000);
};
